import { mdiCalendarOutline, mdiCurrencyUsd, mdiDomain, mdiEyeOffOutline, mdiMagnify, mdiPound, mdiRestore } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { depositPayer, IBankTransaction, TransactionSort, TransactionTab } from '../../services/remits/banking-api';
import { IRemit, RemitKind } from '../../services/remits/remits-api';
import { IMatchOn, ReconcileStore } from '../../stores/remits/reconcile.store';
import { ButtonGroup, IButtonGroupItemProps } from '../base/button-group/button-group';
import { BaseButton } from '../base/button/button';
import { BaseCheckbox } from '../base/checkbox/checkbox';
import { BaseInput } from '../base/input/input';
import { Notice } from '../base/notice/notice';
import { BaseRadio } from '../base/radio/radio';
import { BaseSelect, ISelectOption } from '../base/select/select';
import { Spinner } from '../base/spinner/spinner';
import { day, money, signedMoney } from './remit-format';
import * as S from './reconcile.styles';

const TAB_LABELS: Record<TransactionTab, string> = { UNPOSTED: 'Unposted', POSTED: 'Posted', IGNORED: 'Ignored' };
const SORTS: Array<{ sort: TransactionSort; label: string }> = [
    { sort: 'DATE', label: 'Date' },
    { sort: 'AMOUNT', label: 'Amount' },
    { sort: 'NAME', label: 'Name' },
    { sort: 'CREATED', label: 'Created' },
];

/** What Post Payment does — and does not: billers read "post" as applying the payment to claims. */
const POST_MEANING = 'Links this remit to the bank deposit. It does not post the payment to claims.';

/** A text filter that waits for typing to pause before it asks the server. */
const useDebounced = (value: string, delay = 400): string => {
    const [settled, setSettled] = React.useState(value);
    React.useEffect(() => {
        const timer = window.setTimeout(() => setSettled(value), delay);
        return (): void => window.clearTimeout(timer);
    }, [value, delay]);
    return settled;
};

/** A typed amount as the filter wants it: `$1,495.40` → `1495.40`; anything else is no filter. */
const amountOrUndefined = (text: string): string | undefined => {
    const cleaned = text.replace(/[$,\s]/g, '');
    return /^\d+(\.\d{1,2})?$/.test(cleaned) ? cleaned : undefined;
};

const Field: React.FC<{
    icon: string;
    placeholder: string;
    value: string;
    width?: number;
    grow?: boolean;
    type?: string;
    onChange: (value: string) => void;
}> = ({ icon, placeholder, value, width, grow, type, onChange }) => (
    <S.Slot width={width} grow={grow}>
        <BaseInput
            compact={true}
            iconPath={icon}
            type={type}
            value={value}
            placeholder={placeholder}
            aria-label={placeholder}
            disableLatin1Validation={true}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => onChange(event.target.value)}
        />
    </S.Slot>
);

/** Why "Same payer" can hide the right remit — measured, so the page says it rather than leaving the user to find out. */
const PAYER_CAVEAT =
    'Matches the payer id the bank and the 835 carry, or the payer name. It agreed on about 3 in 4 posted deposits (production, 2026-10-16): payments sent through a vendor carry the vendor, so if the right remit is missing, try without it.';

/** "1 unposted ERA · same amount": what the list holds and what it was matched on. */
const matchSummary = (match: IMatchOn): string =>
    [
        match.amount ? (match.tolerance > 0 ? `within $${match.tolerance.toFixed(2)}` : 'same amount') : '',
        match.paymentNumber ? 'same payment number' : '',
        match.withinDays ? `within ${match.days} days` : '',
        match.payer ? 'same payer' : '',
    ]
        .filter(Boolean)
        .join(' · ');

/**
 * "Match on" (FE-184, Jordan 2026-10-15): the quick filters, on whichever side is being narrowed — the remits for the
 * picked deposit, or, flipped, the deposits for the picked remit. The two numbers wait for typing to pause.
 */
const MatchOnRow: React.FC<{ store: ReconcileStore }> = observer(({ store }) => {
    const match = store.matchOn;
    const [tolerance, setTolerance] = React.useState(String(match.tolerance));
    const [days, setDays] = React.useState(String(match.days));
    const settledTolerance = useDebounced(tolerance);
    const settledDays = useDebounced(days);
    React.useEffect(() => {
        const n = Number(settledTolerance);
        if (settledTolerance.trim() !== '' && Number.isFinite(n) && n >= 0) {
            store.setMatchOn({ tolerance: Math.round(n * 100) / 100 });
        }
    }, [store, settledTolerance]);
    React.useEffect(() => {
        const n = Number(settledDays);
        if (Number.isInteger(n) && n > 0 && n <= 366) {
            store.setMatchOn({ days: n });
        }
    }, [store, settledDays]);
    const toggle =
        (key: 'amount' | 'paymentNumber' | 'withinDays' | 'payer') =>
        (event: React.ChangeEvent<HTMLInputElement>): void =>
            store.setMatchOn({ [key]: event.target.checked });
    // The number to match is the picked side's: the deposit's (FE-183), or, flipped, the remit's.
    const picked = store.flipped ? store.selectedRemit : store.selected;
    const number = picked?.paymentNumber;
    const noNumber =
        !picked || number
            ? undefined
            : store.flipped
            ? 'This remit has no payment number to match.'
            : "This deposit's description carries no payment number to match.";
    return (
        <S.MatchRow data-testid="reconcile-match-on">
            <S.MatchLabel>Match on</S.MatchLabel>
            <S.MatchChip checked={match.amount}>
                <BaseCheckbox label="Payment amount" checked={match.amount} onChange={toggle('amount')} />
                <span>± $</span>
                <S.MatchNumber>
                    <BaseInput
                        compact={true}
                        type="number"
                        min={0}
                        step="0.01"
                        value={tolerance}
                        aria-label="Amount tolerance in dollars"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setTolerance(event.target.value)}
                    />
                </S.MatchNumber>
            </S.MatchChip>
            <S.MatchChip checked={match.paymentNumber && !!number} title={noNumber}>
                <BaseCheckbox
                    label="Same payment number"
                    checked={match.paymentNumber && !!number}
                    disabled={!!picked && !number}
                    onChange={toggle('paymentNumber')}
                />
            </S.MatchChip>
            <S.MatchChip checked={match.withinDays}>
                <BaseCheckbox label="Within" checked={match.withinDays} onChange={toggle('withinDays')} />
                <S.MatchNumber>
                    <BaseInput
                        compact={true}
                        type="number"
                        min={1}
                        max={366}
                        step="1"
                        value={days}
                        aria-label="Days"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setDays(event.target.value)}
                    />
                </S.MatchNumber>
                <span>days</span>
            </S.MatchChip>
            <S.MatchChip checked={match.payer} title={PAYER_CAVEAT}>
                <BaseCheckbox label="Same payer" checked={match.payer} onChange={toggle('payer')} />
            </S.MatchChip>
        </S.MatchRow>
    );
});

/** The question before every Post, either way round: it is a write on the practice's books. */
const postQuestion = (remit: IRemit, deposit: IBankTransaction): string => {
    const gap = Math.round((deposit.deposit - Number(remit.paidAmount || 0)) * 100) / 100;
    return [
        `Post ${remit.kind} ${remit.paymentNumber || remit.displayId} · ${remit.payerName || 'payer not named'} (${money(
            Number(remit.paidAmount || 0),
        )}) to the ${money(deposit.deposit)} deposit of ${day(deposit.date)}?`,
        gap ? `They differ by ${signedMoney(gap)}.` : '',
        'This links the remit to the deposit; it does not post the payment to claims.',
    ]
        .filter(Boolean)
        .join(' ');
};

export interface IReconcilePanelProps {
    store: ReconcileStore;
    /** The console's own confirm step: Unpost always, Post when it leaves a difference (the design asks before Unpost). */
    confirm: (message: string, action: string) => Promise<boolean>;
}

/**
 * The Reconcile tab (FE-163, design 07, crosswalk §4–5): bank deposits on one side, the remits that could pay the selected
 * one on the other.
 *
 * Built from the crosswalk. Not built: "ENTER auto-matches the rest" — the crosswalk defines no auto-match, so the page
 * does not claim one (NS-4) — and the Export, which comes next.
 */
export const ReconcilePanel: React.FC<IReconcilePanelProps> = observer(({ store, confirm }) => {
    const otherAccounts =
        store.unpostedDeposits !== undefined && store.itemId
            ? store.unpostedDeposits - (store.unpostedDepositsByItem.get(store.itemId) || 0)
            : undefined;

    return (
        <>
            <S.Summary data-testid="reconcile-summary">
                <span title="Money in, in every connected account, not yet posted to a remit — all time.">
                    Unposted deposits · all accounts
                    <strong className={store.unpostedDeposits ? 'attention' : undefined}>
                        {store.unpostedDeposits ?? (store.headerError ? '—' : '…')}
                    </strong>
                </span>
                <span title="ERAs and EOBs not yet posted to a deposit — all time, including remits that never reach a connected account.">
                    Unposted remits<strong>{store.unpostedRemits ?? (store.headerError ? '—' : '…')}</strong>
                </span>
            </S.Summary>
            {store.writeError && (
                <S.UndoBar role="alert">
                    <Notice tone="attention">{store.writeError}</Notice>
                    <BaseButton variant="text" size="compact" onClick={store.dismissWriteError}>
                        Dismiss
                    </BaseButton>
                </S.UndoBar>
            )}
            {store.lastUndo && !store.busy && (
                <S.UndoBar role="status">
                    <span>{store.lastUndo.done}</span>
                    <BaseButton variant="text" size="compact" onClick={(): void => void store.undo()}>
                        {store.lastUndo.label}
                    </BaseButton>
                </S.UndoBar>
            )}
            <S.Panels flipped={store.flipped}>
                <Transactions store={store} otherAccounts={otherAccounts} confirm={confirm} />
                <Candidates store={store} confirm={confirm} />
            </S.Panels>
        </>
    );
});

interface ITransactionsProps {
    store: ReconcileStore;
    otherAccounts?: number;
    confirm: IReconcilePanelProps['confirm'];
}

const Transactions: React.FC<ITransactionsProps> = observer(({ store, otherAccounts, confirm }) => {
    const [name, setName] = React.useState('');
    const [amount, setAmount] = React.useState('');
    const [date, setDate] = React.useState('');
    const [externalId, setExternalId] = React.useState('');
    const settledName = useDebounced(name.trim());
    const settledAmount = useDebounced(amount);
    const settledExternal = useDebounced(externalId.trim());
    React.useEffect(() => {
        store.setFilters({
            name: settledName || undefined,
            amount: amountOrUndefined(settledAmount),
            date: date || undefined,
            externalId: settledExternal || undefined,
        });
    }, [store, settledName, settledAmount, date, settledExternal]);

    const accountOptions: Array<ISelectOption<string>> = store.accounts.map(account => ({
        label: account.label,
        value: account.itemId,
    }));
    const tabButtons: IButtonGroupItemProps[] = (Object.keys(TAB_LABELS) as TransactionTab[]).map(tab => ({
        label: `${TAB_LABELS[tab]}${store.counts[tab] !== undefined ? ` ${store.counts[tab]?.toLocaleString()}` : ''}`,
    }));
    const tabs = Object.keys(TAB_LABELS) as TransactionTab[];
    const selectedTab = tabButtons[tabs.indexOf(store.tab)];
    const first = store.pageIndex * store.pageSize;
    const last = Math.min(first + store.transactions.length, store.total);
    const remit = store.flipped ? store.selectedRemit : undefined;
    // Flipped: Post Payment is on the deposit rows, for the remit picked on the other side.
    const postTo = async (deposit: IBankTransaction): Promise<void> => {
        if (remit && (await confirm(postQuestion(remit, deposit), 'Post payment'))) {
            await store.postTo(deposit);
        }
    };

    return (
        <S.Panel data-testid="reconcile-transactions">
            <S.PanelHead>
                <S.Step>{store.flipped ? 2 : 1}</S.Step>
                <div>
                    <h3>Bank Account Transactions</h3>
                    <p>
                        {!store.flipped
                            ? 'Pick the deposit you are reconciling.'
                            : remit
                            ? `Showing deposits that could carry ${remit.kind} ${remit.paymentNumber || remit.displayId} (${money(
                                  Number(remit.paidAmount || 0),
                              )}), paid ${day(remit.paidDate)}.`
                            : 'Pick a remit to see the deposits that could carry it.'}
                    </p>
                </div>
            </S.PanelHead>
            {store.accountsError ? (
                <S.NoticeSlot>
                    <Notice tone="attention">{store.accountsError}</Notice>
                </S.NoticeSlot>
            ) : !store.accounts.length ? (
                <S.Empty>No bank account is connected to this practice yet.</S.Empty>
            ) : (
                <>
                    <S.Controls>
                        <S.Slot width={260}>
                            <BaseSelect
                                required={true}
                                aria-label="Bank account"
                                options={accountOptions}
                                value={accountOptions.find(option => option.value === store.itemId)}
                                onChange={(option): void => {
                                    const value = (option as ISelectOption<string>)?.value;
                                    if (value) {
                                        store.setAccount(value);
                                    }
                                }}
                            />
                        </S.Slot>
                        <ButtonGroup
                            variant="track"
                            buttons={tabButtons}
                            selected={selectedTab}
                            onSelect={(button): void => store.setTab(tabs[tabButtons.indexOf(button)] || 'UNPOSTED')}
                        />
                    </S.Controls>
                    {store.flipped && <MatchOnRow store={store} />}
                    <S.Controls>
                        <Field icon={mdiMagnify} placeholder="Name" value={name} grow={true} onChange={setName} />
                        <Field icon={mdiCurrencyUsd} placeholder="Amount" value={amount} width={130} onChange={setAmount} />
                        <Field
                            icon={mdiCalendarOutline}
                            placeholder="Date"
                            type="date"
                            value={date}
                            width={160}
                            onChange={setDate}
                        />
                    </S.Controls>
                    <S.Controls>
                        <Field icon={mdiPound} placeholder="Transaction ID" value={externalId} grow={true} onChange={setExternalId} />
                        <S.SortLabel>Sort</S.SortLabel>
                        {SORTS.map(({ sort, label }) => (
                            <BaseButton
                                key={sort}
                                variant={store.sort === sort ? 'outlined' : 'text'}
                                size="compact"
                                aria-pressed={store.sort === sort}
                                onClick={(): void => store.setSort(sort)}>
                                {label}
                                {store.sort === sort ? (store.order === 'DESCENDING' ? ' ↓' : ' ↑') : ''}
                            </BaseButton>
                        ))}
                    </S.Controls>
                    <S.ListHead>
                        <span>Transaction</span>
                        <span>Amount</span>
                    </S.ListHead>
                    {store.error ? (
                        <S.NoticeSlot>
                            <Notice tone="attention">{store.error}</Notice>
                        </S.NoticeSlot>
                    ) : store.loading && !store.transactions.length ? (
                        <S.Empty role="status">
                            <Spinner size="14px" /> Loading the transactions&hellip;
                        </S.Empty>
                    ) : !store.visibleTransactions.length ? (
                        <S.Empty>
                            {remit && store.remitPaysNothing
                                ? `${remit.kind} ${remit.paymentNumber || remit.displayId} paid ${money(
                                      Number(remit.paidAmount || 0),
                                  )}, so no deposit carries it: there is nothing to post.`
                                : remit && store.tab === 'UNPOSTED'
                                ? `No unposted deposit ${store.depositsMatchedOnPageOnly ? 'on this page' : 'in this account'} matches ${
                                      matchSummary(store.appliedMatch) ? `on: ${matchSummary(store.appliedMatch)}` : 'these filters'
                                  }. ${
                                      store.depositsMatchedOnPageOnly
                                          ? 'Other pages may: page through them, or narrow the filters above.'
                                          : 'Loosen "Match on" to widen the search.'
                                  }`
                                : store.tab === 'UNPOSTED'
                                ? 'No unposted deposits in this account.'
                                : store.tab === 'POSTED'
                                ? 'No posted deposits in this account.'
                                : 'No ignored transactions in this account.'}
                        </S.Empty>
                    ) : (
                        <div role={store.flipped ? undefined : 'radiogroup'} aria-label="Bank transactions">
                            {store.visibleTransactions.map(transaction => (
                                <TransactionRow key={transaction.id} store={store} transaction={transaction} onPost={postTo} />
                            ))}
                        </div>
                    )}
                    <S.Foot>
                        {/* The design's "Plaid synced 7:15 AM" has no source in the crosswalk, so it is not shown (NS-4). */}
                        <span>
                            {store.tab === 'POSTED'
                                ? 'Posted deposits can be unposted from the remit they are posted to.'
                                : store.tab === 'IGNORED'
                                ? 'Ignored transactions stay out of Unposted until restored.'
                                : `${
                                      store.counts.UNPOSTED !== undefined
                                          ? `${store.counts.UNPOSTED.toLocaleString()} unposted in this account`
                                          : ''
                                  }${otherAccounts ? ` · ${otherAccounts.toLocaleString()} in other accounts` : ''}`}
                        </span>
                        {(store.total > store.pageSize || store.pageIndex > 0) && (
                            <S.Pager>
                                <span>
                                    {first + 1}&ndash;{last} of {store.total.toLocaleString()}
                                </span>
                                <BaseButton
                                    variant="text"
                                    size="compact"
                                    disabled={!store.pageIndex || store.loading}
                                    onClick={(): void => store.setPage(store.pageIndex - 1, store.pageSize)}>
                                    Previous
                                </BaseButton>
                                <BaseButton
                                    variant="text"
                                    size="compact"
                                    disabled={last >= store.total || store.loading}
                                    onClick={(): void => store.setPage(store.pageIndex + 1, store.pageSize)}>
                                    Next
                                </BaseButton>
                            </S.Pager>
                        )}
                    </S.Foot>
                </>
            )}
        </S.Panel>
    );
});

const TransactionRow: React.FC<{
    store: ReconcileStore;
    transaction: IBankTransaction;
    /** Flipped: posts the picked remit to this deposit. */
    onPost: (deposit: IBankTransaction) => Promise<void>;
}> = observer(({ store, transaction, onPost }) => {
    const account = store.accounts.find(each => each.itemId === transaction.itemId);
    const payer = depositPayer(transaction.name);
    // Flipped, the deposit is where the remit goes, not what is picked.
    const selectable = !store.flipped && store.tab !== 'IGNORED';
    const remit = store.flipped ? store.selectedRemit : undefined;
    const postBlocked = !store.writesEnabled
        ? store.writesOff
        : !remit
        ? 'Pick a remit first.'
        : remit.plaidItemTransactionId
        ? 'This remit is already posted to a deposit, and a remit is posted to one. Unpost it first.'
        : store.remitPaysNothing
        ? 'This remit paid nothing, so no deposit carries it.'
        : undefined;
    const selected = store.selected?.id === transaction.id;
    const posted = store.postedTo.get(transaction.id);
    const choose = (): void => {
        if (selectable) {
            store.select(selected ? undefined : transaction);
        }
    };
    return (
        <S.Row
            data-testid="reconcile-transaction"
            selected={selected}
            selectable={selectable}
            onClick={choose}
            onKeyDown={(event): void => {
                // The row's own keys only: Enter on its Ignore button must press the button, not pick the row.
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    choose();
                }
            }}
            tabIndex={selectable ? 0 : -1}
            role={selectable ? 'radio' : undefined}
            aria-checked={selectable ? selected : undefined}>
            {selectable && <BaseRadio checked={selected} readOnly={true} tabIndex={-1} aria-label="Select this deposit" />}
            {/* One line each, whole on hover: an ACH description runs to several hundred characters. */}
            <S.RowMain title={transaction.name}>
                <strong>{payer || transaction.name || 'Bank transaction'}</strong>
                {/* The payment number first, as on a remit row, so the two can be read against each other (FE-183). */}
                <span>
                    {[
                        transaction.paymentNumber && `#${transaction.paymentNumber}`,
                        payer ? transaction.name : undefined,
                        account?.mask ? `••${account.mask}` : undefined,
                        transaction.externalId,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </span>
            </S.RowMain>
            <S.RowAmount>
                {/* Money in shows as a positive deposit; an outflow (only on Posted or Ignored) keeps its minus. */}
                <strong>{money(transaction.deposit)}</strong>
                <span>{day(transaction.date)}</span>
            </S.RowAmount>
            <S.RowAction onClick={(event): void => event.stopPropagation()}>
                {store.tab === 'POSTED' ? (
                    <S.PostedTag>
                        {posted?.length
                            ? `Posted to ${posted.map(remit => `${remit.kind} ${remit.paymentNumber || remit.displayId}`).join(', ')}`
                            : 'Posted'}
                    </S.PostedTag>
                ) : (
                    <>
                        {store.flipped && store.tab === 'UNPOSTED' && (
                            <span title={postBlocked || POST_MEANING}>
                                <BaseButton
                                    variant="primary"
                                    size="compact"
                                    disabled={!!postBlocked || !!store.busy}
                                    onClick={(): void => void onPost(transaction)}>
                                    Post Payment
                                </BaseButton>
                            </span>
                        )}
                        <span title={store.writesEnabled ? undefined : store.writesOff}>
                            <BaseButton
                                variant="text"
                                size="compact"
                                disabled={!store.writesEnabled || !!store.busy}
                                onClick={(): void =>
                                    void (store.tab === 'IGNORED' ? store.restore(transaction) : store.ignore(transaction))
                                }>
                                <Icon path={store.tab === 'IGNORED' ? mdiRestore : mdiEyeOffOutline} size={0.65} />
                                <span>{store.tab === 'IGNORED' ? 'Restore' : 'Ignore'}</span>
                            </BaseButton>
                        </span>
                    </>
                )}
            </S.RowAction>
        </S.Row>
    );
});

const Candidates: React.FC<{ store: ReconcileStore; confirm: IReconcilePanelProps['confirm'] }> = observer(({ store, confirm }) => {
    const [payerName, setPayerName] = React.useState('');
    const [paymentNumber, setPaymentNumber] = React.useState('');
    const [amount, setAmount] = React.useState('');
    const [paidDate, setPaidDate] = React.useState('');
    const settledPayer = useDebounced(payerName.trim());
    const settledNumber = useDebounced(paymentNumber.trim());
    const settledAmount = useDebounced(amount);
    React.useEffect(() => {
        store.setCandidateFilters({
            payerName: settledPayer || undefined,
            paymentNumber: settledNumber || undefined,
            amount: amountOrUndefined(settledAmount),
            paidDate: paidDate || undefined,
        });
    }, [store, settledPayer, settledNumber, settledAmount, paidDate]);

    const kindList: RemitKind[] = ['ERA', 'EOB'];
    const kinds: IButtonGroupItemProps[] = kindList.map(each => ({
        label: `${each}${store.shownKindCounts[each] !== undefined ? ` ${store.shownKindCounts[each]?.toLocaleString()}` : ''}`,
    }));
    const deposit = store.selected;

    const already = store.selectedPosted;
    const unpost = async (remit: IRemit): Promise<void> => {
        const ok = await confirm(
            `Unpost ${remit.kind} ${remit.paymentNumber || remit.displayId} · ${remit.payerName || 'payer not named'} · ${money(
                Number(remit.paidAmount || 0),
            )}? It is no longer linked to its bank deposit and counts as unposted again. A link made by automatic matching cannot be unposted.`,
            'Unpost',
        );
        if (ok) {
            await store.unpost(remit);
        }
    };
    // Every post is asked about: it is a write on the practice's books. Unpost removes a link posted by hand, which is what
    // this makes (proven on production for ERAs and EOBs, NS-17).
    const post = async (remit: IRemit): Promise<void> => {
        if (deposit && (await confirm(postQuestion(remit, deposit), 'Post payment'))) {
            await store.post(remit);
        }
    };
    // The server allows one remit per deposit (measured 2026-10-12): a deposit that already has one cannot take another.
    // Every Posted-tab deposit has one by the tab's own filter, so it is taken even before the lookup naming its remit lands.
    const taken = store.tab === 'POSTED' || !!already?.remits.length;

    return (
        <S.Panel data-testid="reconcile-candidates">
            <S.PanelHead>
                <S.Step>{store.flipped ? 1 : 2}</S.Step>
                <div>
                    <h3>Remits</h3>
                    <p>
                        {store.flipped
                            ? 'Pick the remit you are posting.'
                            : deposit
                            ? `Showing remits that could pay the ${money(deposit.deposit)} deposit from ${day(deposit.date)}.`
                            : 'Pick a deposit on the left to see the remits that could pay it.'}
                    </p>
                    {deposit && already && already.remits.length > 0 && (
                        <p>
                            Already posted to it: {already.remits.map(remit => `${remit.kind} ${remit.paymentNumber || remit.displayId}`).join(', ')}{' '}
                            ({money(already.posted)}) · remaining {signedMoney(already.remaining)}.
                        </p>
                    )}
                </div>
                <S.HeadAside>
                    <S.MatchChip checked={store.showPosted}>
                        <BaseCheckbox
                            label={`Show posted${store.shownPostedMatches !== undefined ? ` (${store.shownPostedMatches})` : ''}`}
                            checked={store.showPosted}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => store.setShowPosted(event.target.checked)}
                        />
                    </S.MatchChip>
                </S.HeadAside>
            </S.PanelHead>
            {!store.flipped && <MatchOnRow store={store} />}
            <S.Controls>
                <ButtonGroup
                    variant="track"
                    buttons={kinds}
                    selected={kinds[kindList.indexOf(store.kind)]}
                    onSelect={(button): void => store.setKind(kindList[kinds.indexOf(button)] || 'ERA')}
                />
            </S.Controls>
            <S.Controls>
                <Field icon={mdiDomain} placeholder="Payer name" value={payerName} grow={true} onChange={setPayerName} />
                <Field icon={mdiPound} placeholder="Payment number" value={paymentNumber} width={150} onChange={setPaymentNumber} />
                <Field icon={mdiCurrencyUsd} placeholder="Paid amount" value={amount} width={130} onChange={setAmount} />
                <Field icon={mdiCalendarOutline} placeholder="Paid date" type="date" value={paidDate} width={160} onChange={setPaidDate} />
            </S.Controls>
            <S.ListHead>
                <span>Remit &amp; posted transaction</span>
                <span>Paid</span>
            </S.ListHead>
            {!deposit && !store.flipped ? (
                <S.Empty>No deposit selected.</S.Empty>
            ) : store.candidatesError ? (
                <S.NoticeSlot>
                    <Notice tone="attention">{store.candidatesError}</Notice>
                </S.NoticeSlot>
            ) : store.candidatesLoading && !store.candidates.length ? (
                <S.Empty role="status">
                    <Spinner size="14px" /> Looking for remits&hellip;
                </S.Empty>
            ) : !store.visibleCandidates.length ? (
                <S.Empty>
                    {!deposit
                        ? `No ${store.showPosted ? '' : 'unposted '}${store.kind} matches these filters.`
                        : `No ${store.showPosted ? '' : 'unposted '}${store.kind} matches ${
                              matchSummary(store.appliedMatch) ? `on: ${matchSummary(store.appliedMatch)}` : 'these filters'
                          }. Loosen "Match on" to widen the search, or check the ${store.kind === 'ERA' ? 'EOB' : 'ERA'} tab.`}
                    {store.uncheckedCandidates > 0 &&
                        ` Only the first ${store.candidates.length} of ${store.candidatesTotal.toLocaleString()} were checked: narrow the filters above to check the rest.`}
                </S.Empty>
            ) : (
                <div role={store.flipped ? 'radiogroup' : undefined} aria-label="Remits">
                    {store.visibleCandidates.map(remit => (
                        <CandidateRow key={remit.id} store={store} remit={remit} taken={taken} onPost={post} onUnpost={unpost} />
                    ))}
                    {store.candidatesTotal > store.candidates.length && (
                        <S.Empty>
                            The first {store.candidates.length} of {store.candidatesTotal.toLocaleString()} — narrow them with the
                            filters above.
                        </S.Empty>
                    )}
                    {deposit && (
                        <S.ListSummary>
                            {[
                                `${store.visibleCandidates.length.toLocaleString()} ${store.showPosted ? '' : 'unposted '}${store.kind}${
                                    store.visibleCandidates.length === 1 ? '' : 's'
                                }`,
                                matchSummary(store.appliedMatch),
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </S.ListSummary>
                    )}
                </div>
            )}
        </S.Panel>
    );
});

const CandidateRow: React.FC<{
    store: ReconcileStore;
    remit: IRemit;
    /** The selected deposit already has a remit: the server takes one per deposit. */
    taken: boolean;
    onPost: (remit: IRemit) => Promise<void>;
    onUnpost: (remit: IRemit) => Promise<void>;
}> = observer(
    ({ store, remit, taken, onPost, onUnpost }) => {
        const ref = remit.plaidItemTransactionId
            ? remit.plaidItemTransactionId === store.selected?.id
                ? store.selected.externalId
                : store.depositRefs.get(remit.plaidItemTransactionId)
            : undefined;
        // The difference from the deposit, on every row that is not exact: a short or over payment is visible before
        // it is posted, not discovered in the export.
        const gap =
            store.selected && !remit.plaidItemTransactionId
                ? Math.round((store.selected.deposit - Number(remit.paidAmount || 0)) * 100) / 100
                : 0;
        // Flipped, the remit is what is picked; Post Payment moves to the deposits.
        const pickable = store.flipped && !remit.plaidItemTransactionId;
        const picked = pickable && store.selectedRemit?.kind === remit.kind && store.selectedRemit?.id === remit.id;
        const pick = (): void => {
            if (pickable) {
                store.selectRemit(picked ? undefined : remit);
            }
        };
        return (
            <S.Row
                data-testid="reconcile-candidate"
                selected={picked}
                selectable={pickable}
                onClick={(event): void => {
                    // The radio is a <label>: a click on it comes again as the hidden input's click. Pick once.
                    if (!(event.target instanceof HTMLInputElement)) {
                        pick();
                    }
                }}
                onKeyDown={(event): void => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        pick();
                    }
                }}
                tabIndex={pickable ? 0 : -1}
                role={pickable ? 'radio' : undefined}
                aria-checked={pickable ? picked : undefined}>
                {pickable && <BaseRadio checked={picked} readOnly={true} tabIndex={-1} aria-label="Select this remit" />}
                <S.RowMain>
                    <strong>{remit.payerName || '—'}</strong>
                    <span>
                        {[remit.paymentNumber && `#${remit.paymentNumber}`, `${remit.kind} ${remit.displayId}`]
                            .filter(Boolean)
                            .join(' · ')}
                        {remit.plaidItemTransactionId ? ` · Posted${ref ? ` · ${ref}` : ''}` : ''}
                    </span>
                </S.RowMain>
                <S.RowAmount>
                    <strong>{money(Number(remit.paidAmount || 0))}</strong>
                    <span>{gap ? `Δ ${signedMoney(gap)} vs deposit` : day(remit.paidDate)}</span>
                </S.RowAmount>
                <S.RowAction onClick={(event): void => event.stopPropagation()}>
                    {pickable ? null : (
                        <span
                            title={
                                !store.writesEnabled
                                    ? store.writesOff
                                    : remit.plaidItemTransactionId
                                    ? undefined
                                    : taken
                                    ? 'This deposit already has a remit posted to it, and the server allows one per deposit.'
                                    : POST_MEANING
                            }>
                            {remit.plaidItemTransactionId ? (
                                <BaseButton
                                    variant="outlined"
                                    size="compact"
                                    disabled={!store.writesEnabled || !!store.busy}
                                    onClick={(): void => void onUnpost(remit)}>
                                    Unpost
                                </BaseButton>
                            ) : (
                                <BaseButton
                                    variant="primary"
                                    size="compact"
                                    disabled={!store.writesEnabled || !!store.busy || taken}
                                    onClick={(): void => void onPost(remit)}>
                                    Post Payment
                                </BaseButton>
                            )}
                        </span>
                    )}
                </S.RowAction>
            </S.Row>
        );
    },
);
